import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { drillsService } from '../../services/drillsService';
import LoadingState from '../../components/ui/LoadingState';
import DrillCard from './components/DrillCard';
import DrillFilters from './components/DrillFilters';
import CreateDrillModal from './components/CreateDrillModal';

export default function DrillsManagement() {
  const { user, profile } = useAuth();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    type: '',
    difficultyLevel: ''
  });

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    loadDrills();
  }, [filters]);

  const loadDrills = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await drillsService?.getAll(filters);
      setDrills(data);
    } catch (err) {
      setError(err?.message || 'Failed to load drills');
      console.error('Error loading drills:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDrill = async (drillData) => {
    try {
      await drillsService?.create(drillData);
      setShowCreateModal(false);
      loadDrills(); // Reload to show new drill
    } catch (err) {
      throw err; // Let modal handle the error
    }
  };

  const handleDeleteDrill = async (drillId) => {
    if (!window.confirm('Are you sure you want to delete this drill?')) return;

    try {
      await drillsService?.delete(drillId);
      loadDrills(); // Reload after deletion
    } catch (err) {
      setError(err?.message || 'Failed to delete drill');
    }
  };

  if (loading) {
    return <LoadingState message="Loading drills..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Practice Drills</h1>
            <p className="text-slate-600 mt-1">
              Browse and practice sales drills to improve your skills
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <span className="text-xl">+</span>
              Create Drill
            </button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <DrillFilters
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* Drills Grid */}
        {drills?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 text-lg">No drills found matching your filters.</p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Create the first drill
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drills?.map(drill => (
              <DrillCard
                key={drill?.id}
                drill={drill}
                isAdmin={isAdmin}
                onDelete={handleDeleteDrill}
              />
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateDrillModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateDrill}
          />
        )}
      </div>
    </div>
  );
}